/**
 * OS Linked Items TanStack Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../api';

// =============================================================================
// Helpers
// =============================================================================

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Keys
// =============================================================================

export const linkedItemKeys = {
  all: ['os-linked-items'] as const,
  list: (sourceType: string, sourceId: string) =>
    [...linkedItemKeys.all, 'list', sourceType, sourceId] as const,
};

// =============================================================================
// Hooks
// =============================================================================

export function useLinkedItems(sourceType: string, sourceId: string) {
  return useQuery({
    queryKey: linkedItemKeys.list(sourceType, sourceId),
    queryFn: () => api.getLinkedItems(sourceType, sourceId),
    enabled: !!sourceType && !!sourceId,
  });
}

export function useCreateLinkedItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      source_type: string;
      source_id: string;
      target_type: string;
      target_id: string;
    }) => api.createLinkedItem(data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({
        queryKey: linkedItemKeys.list(variables.source_type, variables.source_id),
      });
      toast.success('Item linked');
    },
    onError: (e) => toast.error('Failed to link item', { description: getErrorMessage(e) }),
  });
}

export function useDeleteLinkedItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      sourceType: _sourceType,
      sourceId: _sourceId,
    }: {
      id: string;
      sourceType: string;
      sourceId: string;
    }) => api.deleteLinkedItem(id),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({
        queryKey: linkedItemKeys.list(variables.sourceType, variables.sourceId),
      });
      toast.success('Link removed');
    },
    onError: (e) => toast.error('Failed to remove link', { description: getErrorMessage(e) }),
  });
}
